import React from 'react';
import {MapPin, Ticket, DollarSign, Clock} from 'lucide-react';

export default function JapanThemeParks() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="relative h-[400px] overflow-hidden">
        <img
          src="https://images.pexels.com/photos/2166559/pexels-photo-2166559.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt="Japan Theme Parks"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/80 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-5xl font-bold text-white mb-2">Japan Theme Parks</h1>
            <p className="text-xl text-white/90">World-Class Entertainment & Magic</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-[#0A2540] mb-4">Major Theme Parks</h2>
          <p className="text-gray-600 mb-8">
            Japan offers some of the world's most immersive and innovative theme parks, from Disney magic to Universal thrills and unique Japanese-only attractions.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Universal Studios Japan */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-2xl font-bold text-[#0A2540] mb-3">Universal Studios Japan</h3>
              <div className="mb-4">
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <MapPin className="w-4 h-4 mr-2 text-[#FF6B35]" />
                  <span>Osaka</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <DollarSign className="w-4 h-4 mr-2 text-[#F7B32B]" />
                  <span>1-Day Pass: ¥8,600-10,400 ($60-70)</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="w-4 h-4 mr-2 text-[#4A90E2]" />
                  <span>Full day recommended</span>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                Home to The Wizarding World of Harry Potter, Super Nintendo World, and exclusive attractions. Express Passes highly recommended to skip long queues.
              </p>
              <div className="bg-[#F5F5F5] rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-800 mb-2">Top Attractions:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Mario Kart: Koopa's Challenge</li>
                  <li>• Harry Potter and the Forbidden Journey</li>
                  <li>• The Flying Dinosaur</li>
                  <li>• Minion Park</li>
                </ul>
              </div>
            </div>

            {/* Tokyo Disneyland */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-2xl font-bold text-[#0A2540] mb-3">Tokyo Disneyland</h3>
              <div className="mb-4">
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <MapPin className="w-4 h-4 mr-2 text-[#FF6B35]" />
                  <span>Tokyo (Chiba)</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <DollarSign className="w-4 h-4 mr-2 text-[#F7B32B]" />
                  <span>1-Day Pass: ¥7,900-9,400 ($55-65)</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="w-4 h-4 mr-2 text-[#4A90E2]" />
                  <span>Full day recommended</span>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                The "Happiest Place on Earth" with Japanese hospitality. Features unique attractions, shows, and seasonal events not found in other Disney parks worldwide.
              </p>
              <div className="bg-[#F5F5F5] rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-800 mb-2">Top Attractions:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Beauty and the Beast ride</li>
                  <li>• Enchanted Tale of Beauty and the Beast</li>
                  <li>• Space Mountain</li>
                  <li>• Pooh's Hunny Hunt</li>
                </ul>
              </div>
            </div>

            {/* Tokyo DisneySea */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-2xl font-bold text-[#0A2540] mb-3">Tokyo DisneySea</h3>
              <div className="mb-4">
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <MapPin className="w-4 h-4 mr-2 text-[#FF6B35]" />
                  <span>Tokyo (Chiba)</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <DollarSign className="w-4 h-4 mr-2 text-[#F7B32B]" />
                  <span>1-Day Pass: ¥7,900-9,400 ($55-65)</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="w-4 h-4 mr-2 text-[#4A90E2]" />
                  <span>Full day recommended</span>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                One-of-a-kind Disney park themed around ocean exploration. More adult-oriented with sophisticated theming, unique attractions, and the world's best Disney park according to many fans.
              </p>
              <div className="bg-[#F5F5F5] rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-800 mb-2">Top Attractions:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Journey to the Center of the Earth</li>
                  <li>• Tower of Terror</li>
                  <li>• Soaring: Fantastic Flight</li>
                  <li>• Toy Story Mania!</li>
                </ul>
              </div>
            </div>

            {/* Fuji-Q Highland */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-2xl font-bold text-[#0A2540] mb-3">Fuji-Q Highland</h3>
              <div className="mb-4">
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <MapPin className="w-4 h-4 mr-2 text-[#FF6B35]" />
                  <span>Near Mt. Fuji</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <DollarSign className="w-4 h-4 mr-2 text-[#F7B32B]" />
                  <span>Entry Free, Rides: ¥1,000-2,000 each</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="w-4 h-4 mr-2 text-[#4A90E2]" />
                  <span>Half to full day</span>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                Extreme thrill rides with Mt. Fuji views. Home to record-breaking roller coasters and terrifying haunted attractions. Perfect for adrenaline junkies.
              </p>
              <div className="bg-[#F5F5F5] rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-800 mb-2">Top Attractions:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Do-Dodonpa (world's fastest acceleration)</li>
                  <li>• Fujiyama roller coaster</li>
                  <li>• Eejanaika 4D coaster</li>
                  <li>• Super Scary Labyrinth of Fear</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="bg-gradient-to-r from-[#4A90E2] to-[#0A2540] rounded-xl p-8 text-white">
          <h3 className="text-2xl font-bold mb-4">Planning Tips</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Ticket className="w-6 h-6 mb-2" />
              <h4 className="font-semibold mb-2">Book in Advance</h4>
              <p className="text-sm text-white/90">Tickets sell out for popular dates. Purchase online weeks ahead, especially for Disney parks.</p>
            </div>
            <div>
              <Clock className="w-6 h-6 mb-2" />
              <h4 className="font-semibold mb-2">Arrive Early</h4>
              <p className="text-sm text-white/90">Gates open 30-60 minutes before official opening. Early arrival = shorter wait times.</p>
            </div>
            <div>
              <DollarSign className="w-6 h-6 mb-2" />
              <h4 className="font-semibold mb-2">Express Passes</h4>
              <p className="text-sm text-white/90">Worth the extra cost at USJ. Disney uses free FastPass/Premier Access system.</p>
            </div>
            <div>
              <MapPin className="w-6 h-6 mb-2" />
              <h4 className="font-semibold mb-2">Avoid Weekends</h4>
              <p className="text-sm text-white/90">Weekdays have significantly shorter wait times. Japanese public holidays are extremely crowded.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
